import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Camera, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AvatarDisplay from "./AvatarDisplay";

/**
 * Brutalist Vault ID Card. Renders a member-style badge that can be exported
 * as a PNG (html2canvas). Used on both /profile (with download button) and
 * /u/:username (read-only).
 *
 * Props: username, joinedAt (ISO), rank ({title, color, tagline}), sigilCount,
 *        trustScore (0-100 or null), exportable (bool), avatarId (string|null).
 */
export default function VaultIDCard({
  username,
  joinedAt,
  rank,
  sigilCount,
  trustScore = null,
  exportable = false,
  avatarId = null,
  aiAvatarId = null,
  accentHex = null,
  bio = null,
  tagline = null,
  memberNumber = null,
}) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Personal accent override → falls back to rank color → falls back to brand purple.
  const accent = accentHex || rank?.color || "#a78bfa";
  // Server-issued unique sequential member number (preferred). Fall back to a
  // deterministic hash of the username for legacy users not yet backfilled
  // (the startup migration assigns one on the next boot, so this fallback
  // disappears for real users after one deploy).
  const memberNumberDisplay = (() => {
    if (memberNumber != null && memberNumber !== "") {
      return `#${String(memberNumber).padStart(6, "0")}`;
    }
    if (!username) return "#000000";
    const n = parseInt(
      Array.from(username).reduce((acc, c) => acc + c.charCodeAt(0), 0).toString(),
      10
    ) % 100000;
    return `#${String(n).padStart(6, "0")}`;
  })();
  const joinedDate = joinedAt
    ? new Date(joinedAt).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }).toUpperCase()
    : "—";
  const issuedDate = new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }).toUpperCase();
  // Clearance level mirrors trust band — 4-tier scale for visual variety.
  const clearance = trustScore == null
    ? "L0 · OBSERVER"
    : trustScore >= 80 ? "L4 · ARCHITECT"
    : trustScore >= 60 ? "L3 · ARCHIVIST"
    : trustScore >= 30 ? "L2 · OPERATOR"
    : "L1 · INITIATE";
  // Tiny deterministic "barcode" pattern based on username — visual flavor only.
  const barcode = (() => {
    const seed = (username || "anon").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    const pattern = [];
    for (let i = 0; i < 48; i++) {
      const w = ((seed * (i + 1)) % 7) + 1;        // 1–7 px wide
      const dark = ((seed >> i) ^ i) % 2 === 0;
      pattern.push({ w, dark });
    }
    return pattern;
  })();

  const downloadPng = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      // Wait for the heading + mono web fonts to be parsed/loaded before snapshotting
      // — html2canvas otherwise grabs a partially-rendered first frame which leaves
      // the title clipped (the bug visible in the user's earlier export).
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch { /* no-op */ }
      }
      // Small paint tick so any just-resolved fonts get committed.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#050505",
        scale: 3,        // crisp on retina + Twitter/X compression
        useCORS: true,
        logging: false,
        windowWidth: cardRef.current.scrollWidth,
        windowHeight: cardRef.current.scrollHeight,
      });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vault-id-${username}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("ID downloaded");
    } catch (e) {
      toast.error("Couldn't render card");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="vault-id-card">
      <div
        ref={cardRef}
        className="relative border-2 bg-[#0a0a0a] bg-gradient-to-br from-[#0a0a0a] via-[#0F0F0F] to-[#0a0a0a] p-6 sm:p-8 overflow-hidden"
        style={{ borderColor: `${accent}66` }}
      >
        {/* Top accent stripe — surfaces the user's chosen color */}
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: accent }} />
        {/* Watermark VJ in corner */}
        <div
          className="absolute -right-12 -top-12 font-heading font-black text-[140px] leading-none select-none pointer-events-none"
          style={{ color: `${accent}0a` }}
          aria-hidden
        >
          VJ
        </div>
        {/* Diagonal "CONFIDENTIAL" stamp — gives the PNG real document feel */}
        <div
          className="absolute right-4 top-1/2 -translate-y-1/2 rotate-[18deg] pointer-events-none select-none"
          aria-hidden
        >
          <div
            className="font-heading font-black uppercase tracking-[0.15em] text-[40px] sm:text-[52px] leading-none px-3 py-1 border-[3px]"
            style={{ color: `${accent}24`, borderColor: `${accent}24` }}
          >
            CONFIDENTIAL
          </div>
        </div>
        <div className="relative">
          {/* Top header row */}
          <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6">
            <div className="font-mono text-[10px] tracking-[0.3em]" style={{ color: accent }}>
              V. JONES VAULT // MEMBER ID
            </div>
            <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
              FILE {memberNumberDisplay}
            </div>
          </div>

          {/* Avatar + name + rank — tightened so the title line never clips */}
          <div className="flex items-center gap-5 flex-wrap">
            <div
              className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center border-2"
              style={{ color: accent, backgroundColor: `${accent}0d`, borderColor: `${accent}88` }}
            >
              <AvatarDisplay avatarId={avatarId} aiAvatarId={aiAvatarId} username={username} size={64} />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl truncate"
                style={{ lineHeight: 1.1, paddingBottom: 4, color: accent, textShadow: `0 0 24px ${accent}33` }}
              >
                {username}
              </div>
              {tagline ? (
                <div
                  className="mt-1 font-mono text-[12px] italic truncate"
                  style={{ color: `${accent}cc` }}
                >
                  "{tagline}"
                </div>
              ) : null}
              {rank && (
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-[0.25em] border"
                    style={{ color: rank.color, borderColor: `${rank.color}80`, backgroundColor: `${rank.color}10` }}
                  >
                    <ShieldCheck size={11} /> {rank.title}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 italic">
                    {rank.tagline}
                  </span>
                </div>
              )}
              {/* Secondary row — clearance + issued — keeps the right side of the card alive */}
              <div className="mt-3 flex items-center gap-x-5 gap-y-1 flex-wrap font-mono text-[9px] tracking-[0.25em] text-zinc-500">
                <span>CLEARANCE · <span style={{ color: accent }}>{clearance}</span></span>
                <span>ISSUED · <span className="text-zinc-300">{issuedDate}</span></span>
              </div>
            </div>
          </div>

          {/* Bio — embedded inside the card, accent-themed left rule */}
          {bio ? (
            <div
              className="mt-5 pl-3 border-l-2 font-mono text-[12px] sm:text-[13px] leading-relaxed text-zinc-200 whitespace-pre-wrap"
              style={{ borderColor: accent }}
            >
              {bio}
            </div>
          ) : null}

          {/* Stat grid — joined / sigils / trust */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            <Field label="JOINED" value={joinedDate} accent={accent} />
            <Field label="SIGILS" value={String(sigilCount ?? 0)} accent={accent} />
            <Field
              label="TRUST"
              value={trustScore !== null && trustScore !== undefined ? `${trustScore}/100` : "—"}
              accent={trustScore >= 60 ? "#00ff88" : trustScore >= 30 ? "#ffd60a" : "#94a3b8"}
            />
          </div>

          {/* Decorative "barcode" — pure CSS pattern, no fonts to break */}
          <div className="mt-6 flex items-end gap-[2px] h-9 overflow-hidden" aria-hidden>
            {barcode.map((b, i) => (
              <span
                key={i}
                style={{
                  width: `${b.w}px`,
                  height: "100%",
                  backgroundColor: b.dark ? "#e5e7eb" : "transparent",
                  display: "inline-block",
                }}
              />
            ))}
          </div>
          <div className="mt-2 font-mono text-[8px] tracking-[0.4em] text-zinc-600">
            {memberNumberDisplay.replace("#", "")} · VAULT · {(username || "—").toUpperCase()}
          </div>

          {/* Footer */}
          <div className="mt-5 flex items-center justify-between flex-wrap gap-2 border-t border-white/10 pt-3">
            <div className="font-mono text-[9px] tracking-[0.3em] text-zinc-500">
              VJONESVAULT.COM/U/{username?.toUpperCase()}
            </div>
            <div className="font-mono text-[9px] tracking-[0.3em]" style={{ color: `${accent}b3` }}>
              CONFIDENTIAL · NEED-TO-KNOW
            </div>
          </div>
        </div>
      </div>

      {exportable && (
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={downloadPng}
            disabled={busy}
            data-testid="vault-id-export-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#a78bfa] hover:bg-white text-[#050505] font-mono text-[10px] tracking-[0.25em] uppercase disabled:opacity-60"
          >
            <Download size={12} /> {busy ? "RENDERING…" : "DOWNLOAD AS PNG"}
          </button>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(`${window.location.origin}/u/${username}`);
              toast.success("Public profile link copied");
            }}
            data-testid="vault-id-share-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#050505] border border-[#a78bfa]/50 text-[#a78bfa] hover:text-white hover:border-white/50 font-mono text-[10px] tracking-[0.25em] uppercase"
          >
            <Camera size={12} /> COPY PUBLIC LINK
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, accent }) {
  return (
    <div className="border border-white/10 bg-[#050505] px-3 py-2">
      <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500">{label}</div>
      <div
        className="font-heading text-xl text-white mt-0.5 truncate"
        style={accent ? { color: accent } : {}}
      >
        {value}
      </div>
    </div>
  );
}
